/**
 * E-sign workspace (Brief 185) — the customer's envelopes (read view over
 * GET /v1/esigndoc/envelopes). Creating + sending envelopes is an agent/API
 * flow (POST /v1/esigndoc/envelopes); this surface is the audit + status view.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { FileSignature } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { esignListKey, fetchEsignEnvelopes } from '@/lib/queries';
import { ApiError } from '@/lib/fetcher';

function fmtDate(s?: string): string {
  if (!s) return '—';
  try {
    return format(parseISO(s), 'MMM d, yyyy');
  } catch {
    return s;
  }
}

export function EsignWorkspace() {
  const [page, setPage] = React.useState({ limit: 20, offset: 0 });
  const q = useQuery({ queryKey: esignListKey(page), queryFn: () => fetchEsignEnvelopes(page) });
  const rows = q.data?.data ?? q.data?.envelopes ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">E-sign</h1>
        <p className="text-muted-foreground">
          Your signature envelopes and their status. Send a new one with POST
          /v1/esigndoc/envelopes from any agent or API key.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Envelopes</CardTitle>
          <CardDescription>Newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load envelopes ({q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => q.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileSignature}
              title="No envelopes yet"
              description="Send a document for signature via POST /v1/esigndoc/envelopes — it appears here with live status."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Envelope</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">
                        {e.title ?? e.subject ?? e.id}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{e.status ?? 'unknown'}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {fmtDate(e.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {q.data?.total !== undefined ? (
                <Pagination
                  total={q.data.total}
                  limit={q.data.limit ?? page.limit}
                  offset={q.data.offset ?? page.offset}
                  onChange={setPage}
                />
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
